import React, { useEffect } from 'react';
import { css } from 'emotion';
import { useSelector } from 'react-redux';
import { DropdownField, Text, useForm } from '@weave/design-system';
import { Source } from '@weave/schema-gen-ts/dist/schemas/integrations/integrations-auth-api/v1/service.pb';
import { SourceContainerConfig } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { CopyToClipboardButton } from 'components/shared/copy-to-clipboard/copy-to-clipboard-button';
import { formatDate } from 'helpers/date';
import { theme } from '@weave/theme';
import { useAlert } from '@weave/alert-system';
import { SyncCommandControlQueries } from 'apis/sync-engine-sources/queries';
import { selectHasWeaveAcl } from 'redux/actions/auth/auth.selectors';
import { CoreACLs } from 'redux/actions/auth/auth.types';
import { Store } from 'redux/store/store.model';
import { SyncEngineActions } from './sync-engine-actions.component';
//import { SyncEngineLogsTerminal } from './sync-engine-logs-terminal.component';
import { SyncEngineStatusInfo } from './sync-engine-status-info.component';

interface SyncEngineCardProps {
  source: Source;
  config?: SourceContainerConfig;
}

const CONTAINER_VALUE = 'CONTAINER';

const EMPTY_SYNC_ENGINES: NonNullable<SourceContainerConfig['syncEngines']> = [];

export const SyncEngineCard = ({ source, config }: SyncEngineCardProps) => {
  const alerts = useAlert();

  const syncAppManageAdvancedPermission = useSelector((state: Store) =>
    selectHasWeaveAcl(state, CoreACLs.SYNCAPPMANAGEADVANCED)
  );

  const { getFieldProps, values, seedValues } = useForm({
    fields: {
      syncEngineId: { type: 'dropdown', value: '' },
    },
  });

  const pmsName = config?.pmsName || config?.name;

  const syncEngines = config?.syncEngines ?? EMPTY_SYNC_ENGINES;

  const selectedSyncEngineId = values.syncEngineId ?? '';

  // Seed the sync-engine dropdown once, when the list first arrives.
  useEffect(() => {
    const firstEngineId = syncEngines[0]?.syncEngineId;
    if (firstEngineId && !selectedSyncEngineId) {
      seedValues({ syncEngineId: firstEngineId });
    }
  }, [syncEngines, selectedSyncEngineId]);

  const syncEngineFieldProps = getFieldProps('syncEngineId');
  const isContainerSelected = syncEngineFieldProps.value === CONTAINER_VALUE;

  const selectedEngine = isContainerSelected
    ? undefined
    : syncEngines.find((eng) => eng.syncEngineId === syncEngineFieldProps.value);

  const targetEngineId = isContainerSelected ? '' : selectedEngine?.syncEngineId ?? '';

  const targetName = isContainerSelected
    ? 'Container'
    : selectedEngine?.name || 'Realtime Engine';

  const { refetch: refetchPing, isFetching: isPinging } =
    SyncCommandControlQueries.usePingSyncCommandControl(
      source.sourceId ?? '',
      targetEngineId,
      {
        onSuccess: (data) => {
          if (data?.success) {
            alerts.success(`Ping is successful for ${targetName}`);
          } else {
            alerts.error(`Ping failed for ${targetName}`);
          }
        },
        onError: (error) => {
          alerts.error(`Ping failed for ${targetName}: ${error.message}`);
        },
      }
    );
  const { refetch: refetchRestart, isFetching: isRestarting } =
    SyncCommandControlQueries.useRestartSyncCommandControl(
      source.sourceId ?? '',
      targetEngineId,
      {
        onSuccess: (data) => {
          if (data?.success) {
            alerts.success(`Restart is successful for ${targetName}`);
          } else {
            alerts.error(`Restart failed for ${targetName}`);
          }
        },
        onError: (error) => {
          alerts.error(`Restart failed for ${targetName}: ${error.message}`);
        },
      }
    );

  return (
    <div className={container}>
      <div className={headerRow}>
        {source.type && (
          <Text as="div" size="medium" className={fullTextChip}>
            {source.type}
          </Text>
        )}
      </div>

      <div className={infoGrid}>
        <Text as="span" size="medium" color="light">
          PM Details:
        </Text>
        <Text as="span" size="medium">
          {pmsName || 'N/A'}
        </Text>

        <Text as="span" size="medium" color="light">
          Provisioning Status:
        </Text>
        <Text as="span" size="medium">
          {source.provisioningStatus || 'N/A'}
        </Text>

        <Text as="span" size="medium" color="light">
          Source ID:
        </Text>
        <Text as="span" size="medium" className={copyWrapper}>
          <CopyToClipboardButton label="Copy source id">
            {source.sourceId ?? ''}
          </CopyToClipboardButton>
        </Text>

        <Text as="span" size="medium" color="light">
          Created:
        </Text>
        <Text as="span" size="medium">
          {source.createdAt
            ? formatDate(source.createdAt, 'dddd, DD MMM YYYY, HH:mm:ss', true)
            : 'N/A'}
        </Text>

        <Text as="span" size="medium" color="light">
          Updated:
        </Text>
        <Text as="span" size="medium">
          {source.updatedAt
            ? formatDate(source.updatedAt, 'dddd, DD MMM YYYY, HH:mm:ss', true)
            : 'N/A'}
        </Text>

        <Text as="span" size="medium" color="light">
          Deleted:
        </Text>
        <Text as="span" size="medium">
          {source.deletedAt
            ? formatDate(source.deletedAt, 'dddd, DD MMM YYYY, HH:mm:ss', true)
            : 'N/A'}
        </Text>

        {source.sourceId && (
          <div className={enginesStatusContainer}>
            <SyncEngineStatusInfo
              sourceId={source.sourceId}
              engineId=""
              name="Container"
            />
            {syncEngines.map((engine) => (
              <SyncEngineStatusInfo
                key={engine.syncEngineId}
                sourceId={source.sourceId!}
                engineId={engine.syncEngineId ?? ''}
                name={engine.name || engine.syncEngineId || 'Engine'}
              />
            ))}
          </div>
        )}
      </div>

      {syncAppManageAdvancedPermission && (
        <>
          <div className={selectContainerEngineWrapper}>
            <DropdownField {...syncEngineFieldProps} label="Select Container / Engines">
              <DropdownField.Option value={CONTAINER_VALUE}>
                Container
              </DropdownField.Option>
              {syncEngines.map((engine) => (
                <DropdownField.Option
                  key={engine.syncEngineId}
                  value={engine.syncEngineId ?? ''}
                >
                  {engine.name || engine.syncEngineId}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </div>

          <SyncEngineActions
            sourceId={source.sourceId}
            targetName={targetName}
            isPinging={isPinging}
            isRestarting={isRestarting}
            onPing={() => refetchPing()}
            onRestart={() => refetchRestart()}
          />
        </>
      )}

      {/* <SyncEngineLogsTerminal sourceId={source.sourceId} /> */}
    </div>
  );
};

const container = css`
  border: 1px solid ${theme.colors.neutral20};
  padding: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  margin-bottom: ${theme.spacing(2)};
  background-color: ${theme.colors.white};
`;

const headerRow = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1.5)};
  margin-bottom: ${theme.spacing(1.5)};
`;

const selectContainerEngineWrapper = css`
  margin-top: ${theme.spacing(3)};
  max-width: 320px;
`;

const infoGrid = css`
  display: grid;
  grid-template-columns: 160px 1fr;
  row-gap: ${theme.spacing(0.75)};
  column-gap: ${theme.spacing(1.5)};
  margin-bottom: ${theme.spacing(2)};
`;

const copyWrapper = css`
  display: inline-flex;
  align-items: center;

  button {
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    min-width: ${theme.spacing(3)};
    padding: 0;

    svg {
      width: ${theme.spacing(1.75)};
      height: ${theme.spacing(1.75)};
    }
  }
`;

const fullTextChip = css`
  background-color: ${theme.colors.primary10};
  color: ${theme.colors.neutral60};
  padding: ${theme.spacing(0.25, 1)};
  height: auto;
`;

const enginesStatusContainer = css`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
  margin-top: ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(2)};
`;
